import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ampli } from '@/ampli';
import { useState } from 'react';
import { useMedia } from 'react-use';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';

import { PostFeedbackToast } from '@/components/TransactionToast/PostFeedbackToast';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { buttons } from '@/styles/buttons';
import { inputStyles } from '@/styles/input';
import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import { ShieldTickIcon } from '@/ui/Icons/ShieldTickIcon';
import { WarningCircleSolidIcon } from '@/ui/Icons/WarningCircleSolidIcon';
import { XIcon } from '@/ui/Icons/XIcon';
import { Modal } from '@/ui/Modal';
import { BottomPanelModal } from '@/ui/Modal/BottomPanelModal';

import { triggerFeedbackHint } from '@/utils/feedback-hint';
import { routes } from '@/utils/routes';

interface FeedbackModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  triggerSource: 'toast' | 'menu' | 'portfolio';
  chainId?: string;
  txHash?: string;
}

type ContactMethod = 'email' | 'farcaster' | 'telegram';

const CONTACT_METHODS = [
  { value: 'email' as const, label: 'Email' },
  { value: 'farcaster' as const, label: 'Farcaster' },
  { value: 'telegram' as const, label: 'Telegram' },
];

export function FeedbackModal({
  isOpen,
  onOpenChange,
  onClose,
  triggerSource,
  chainId,
  txHash,
}: FeedbackModalProps) {
  const isMobile = useMedia(breakpoints.mobile, false);
  const { base: popoverStyles, item: popoverItemStyles } = popover();
  const styles = inputStyles();
  const { ethereumWallet, solanaWallet } = useMatchaWallets();

  const [feedback, setFeedback] = useState('');
  const [contact, setContact] = useState('');
  const [contactMethod, setContactMethod] = useState<ContactMethod>('email');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Prevent Radix Dialog from intercepting pointer events (use capture phase)
  const handlePointerDownCapture = (e: React.PointerEvent) => {
    // Force focus on the actual target element
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
      e.stopPropagation();
      // Small delay to ensure click completes before focusing
      setTimeout(() => {
        target.focus();
      }, 0);
    }
  };

  const handleClose = () => {
    ampli.feedbackModalDismissed();
    setFeedback('');
    setContact('');
    setContactMethod('email');
    setError(null);
    setEmailError(null);
    if (triggerSource === 'toast') triggerFeedbackHint();
    onClose();
  };

  const handleSubmit = async () => {
    const walletAddress = ethereumWallet?.address || solanaWallet?.address;
    ampli.feedbackSubmitted({
      feedback_text: feedback.trim(),
      contact_type: contact.trim() ? contactMethod : undefined,
      contact_provided: contact.trim() || undefined,
    });
    if (!walletAddress) {
      setError('No wallet connected');
      return;
    }

    // Validate email if contact method is email and contact info is provided
    if (contact.trim() && contactMethod === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact.trim())) {
        setEmailError('Please enter a valid email address');
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);
    setEmailError(null);

    try {
      const response = await fetch(routes.api.FEEDBACK_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress,
          feedbackText: feedback.trim(),
          triggerSource,
          chainId,
          txHash,
          contactType: contact.trim() ? contactMethod : undefined,
          contactInfo: contact.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit feedback');
      }

      // Success - close modal and reset form
      const hadContactInfo = !!contact.trim();
      setFeedback('');
      setContact('');
      setContactMethod('email');
      setError(null);
      setEmailError(null);
      triggerFeedbackHint();
      onClose();

      // Show success toast using Sonner
      toast.custom(
        (toastId) => (
          <PostFeedbackToast
            hasContactInfo={hadContactInfo}
            onClose={() => toast.dismiss(toastId)}
          />
        ),
        {
          duration: Infinity,
          unstyled: true,
        },
      );
    } catch (err) {
      logger.error('Error submitting feedback:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactMethodSelection = (method: ContactMethod) => {
    setContactMethod(method);
  };

  const selectedContactMethod = CONTACT_METHODS.find((method) => method.value === contactMethod);

  const content = (
    <>
      <Dialog.Close
        className={buttons({
          size: 'small',
          color: 'transparent',
          shape: 'rounded',
          iconOnly: true,
          class: '-mr-3 -my-2 top-6 absolute right-6 hidden tablet:block',
        })}
        aria-label="close feedback dialog"
      >
        <XIcon className="h-4 w-4" />
      </Dialog.Close>

      <div className="p-6" onPointerDownCapture={handlePointerDownCapture}>
        <Dialog.Title className={fontStyles({ variant: 'H5', class: 'mb-1' })}>
          Give Feedback
        </Dialog.Title>
        <Dialog.Description
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'text-token-gray-800 mb-6',
          })}
        >
          {`We'd love to hear your thoughts so we can shape Matcha to better fit your needs.`}
        </Dialog.Description>

        <div className="mb-6">
          <label
            className={fontStyles({
              variant: 'LABEL_SM',
              class: 'mb-2 block text-token-gray-1200',
            })}
          >
            What would you improve?
          </label>
          <div className={styles.root({ class: 'min-h-[120px] items-start' })}>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Type feedback, suggestion or feature request."
              className={`${styles.input()} min-h-[120px] resize-none pt-1.5`}
              maxLength={1000}
            />
          </div>
        </div>

        <div>
          <label
            className={fontStyles({
              variant: 'LABEL_SM',
              class: 'mb-2 block text-token-gray-1200',
            })}
          >
            {`Can we reach out for follow up questions?`}
            <span
              className={fontStyles({
                variant: 'LABEL_SM',
                class: 'text-token-gray-700',
              })}
            >{` (Optional)`}</span>
          </label>

          <div className="flex gap-2">
            <DropdownMenu.Root modal={false}>
              <DropdownMenu.Trigger
                className={buttons({
                  size: 'small',
                  color: 'alphaGray',
                  shape: 'rounded',
                  class:
                    'justify-between gap-1 m-0 px-2.5 min-w-[143px] rounded-sm before:content-none after:content-none',
                })}
              >
                <span className={fontStyles({ variant: 'LABEL_MD' })}>
                  {selectedContactMethod?.label}
                </span>
                <span className="flex justify-center">
                  <ChevronDownIcon />
                </span>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className={popoverStyles({ class: 'mt-0' })}
                  align="start"
                  style={{ minWidth: 'var(--radix-dropdown-menu-trigger-width)' }}
                >
                  {CONTACT_METHODS.map((method) => (
                    <DropdownMenu.Item
                      key={method.value}
                      className={popoverItemStyles()}
                      onClick={() => handleContactMethodSelection(method.value)}
                    >
                      <span className={fontStyles({ variant: 'BODY_SM' })}>{method.label}</span>
                      {method.value === contactMethod && <CheckmarkIcon className="ml-auto" />}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>

            <div className="flex flex-col gap-2 flex-1">
              <div
                className={styles.root({
                  error: !!emailError,
                })}
              >
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => {
                    setContact(e.target.value);
                    setEmailError(null);
                  }}
                  placeholder={contactMethod === 'email' ? 'vitalik@matcha.xyz' : '@vitalik'}
                  className={styles.input()}
                />
                {emailError && (
                  <WarningCircleSolidIcon
                    className={`w-4 h-4 shrink-0 ${styles.accessoryRight({ error: true })}`}
                  />
                )}
              </div>
              {emailError && (
                <div
                  className={fontStyles({
                    variant: 'LABEL_SM',
                    class: 'text-token-red-900',
                  })}
                >
                  {emailError}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex pt-3.5 items-center">
          <ShieldTickIcon />
          <div
            className={fontStyles({
              variant: 'CAPTION',
              class: 'pl-2 text-token-gray-800',
            })}
          >
            We only store your contact information to follow up on your feedback.
          </div>
        </div>
      </div>

      <div
        className="flex flex-col items-end gap-2 p-6 w-full border-t border-special-border-divider"
        onPointerDownCapture={handlePointerDownCapture}
      >
        {error && (
          <div className={fontStyles({ variant: 'BODY_SM', class: 'text-token-red-900 w-full' })}>
            {error}
          </div>
        )}
        <button
          disabled={!feedback.trim() || isSubmitting}
          className={buttons({
            color: 'purple',
            size: 'small',
            shape: 'rounded',
            class: 'min-w-20',
          })}
          onClick={handleSubmit}
        >
          {isSubmitting ? 'Submitting...' : 'Submit feedback'}
        </button>
      </div>
    </>
  );

  return (
    <>
      {isMobile ? (
        <BottomPanelModal
          variant="LARGE"
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          onClose={handleClose}
          allowGestures={isMobile}
        >
          {content}
        </BottomPanelModal>
      ) : (
        <Modal variant="LARGE" isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleClose}>
          {content}
        </Modal>
      )}
    </>
  );
}
